import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const DOT = { full: 'bg-emerald-500', partial: 'bg-amber-400', none: 'bg-red-500', cancelled: 'bg-slate-300' };
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Month grid of games coloured by staffing. Clicking a day shows that day in the list view.
export const OfficialsCalendar = ({ games, month, onMonth, onPickDay }) => {
  const byDay = useMemo(() => {
    const m = {};
    games.forEach(g => { if (g.date) (m[g.date.slice(0, 10)] = m[g.date.slice(0, 10)] || []).push(g); });
    return m;
  }, [games]);
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = new Date(first);
  start.setDate(1 - ((first.getDay() + 6) % 7));
  const cells = Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  const today = ymd(new Date());
  return (
    <div className="bg-white rounded-xl border" data-testid="officials-calendar">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <Button variant="ghost" size="sm" onClick={() => onMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} data-testid="calendar-prev"><ChevronLeft className="w-4 h-4" /></Button>
        <div className="font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>
          {month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }).toUpperCase()}
        </div>
        <Button variant="ghost" size="sm" onClick={() => onMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} data-testid="calendar-next"><ChevronRight className="w-4 h-4" /></Button>
      </div>
      <div className="grid grid-cols-7 text-[10px] font-bold uppercase text-slate-400 text-center py-1.5 border-b">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {cells.map(d => {
          const k = ymd(d);
          const list = byDay[k] || [];
          const inMonth = d.getMonth() === month.getMonth();
          return (
            <button key={k} type="button" onClick={() => list.length && onPickDay(k)} data-testid={`calendar-day-${k}`}
              className={`min-h-[72px] sm:min-h-[92px] border-b border-r p-1 text-left align-top transition-colors ${inMonth ? '' : 'bg-slate-50/70 text-slate-300'} ${list.length ? 'hover:bg-[#00247D]/5' : 'cursor-default'}`}>
              <div className={`text-xs font-semibold mb-1 ${k === today ? 'text-white bg-[#00247D] rounded-full w-5 h-5 flex items-center justify-center' : ''}`}>{d.getDate()}</div>
              <div className="space-y-0.5">
                {list.slice(0, 3).map(g => (
                  <div key={g.match_id} className="flex items-center gap-1 text-[10px] leading-tight text-slate-700 truncate">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${DOT[g.staffing]}`} />
                    <span className="truncate hidden sm:inline">{g.time} {g.team_a_name}</span>
                  </div>
                ))}
                {list.length > 3 && <div className="text-[10px] text-slate-500 font-semibold">+{list.length - 3} more</div>}
              </div>
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-4 px-4 py-2.5 text-[11px] text-slate-500 border-t">
        {[['full', 'Fully staffed'], ['partial', 'Partially staffed'], ['none', 'No officials'], ['cancelled', 'Cancelled']].map(([k, l]) => (
          <span key={k} className="flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${DOT[k]}`} />{l}</span>
        ))}
      </div>
    </div>
  );
};
